'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

import { useMapEvent } from 'react-leaflet';

import { useIsClient } from './providers/IsClientContextProvider';

// Import Leaflet CSS only on client side
if (typeof window !== 'undefined') {
  require('leaflet/dist/leaflet.css');
  // Fix for marker icons
  const L = require('leaflet');
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl:
      'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl:
      'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl:
      'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  });
}

const MapContainer = dynamic(
  () => import('react-leaflet').then((mod) => mod.MapContainer),
  { ssr: false },
);
const TileLayer = dynamic(
  () => import('react-leaflet').then((mod) => mod.TileLayer),
  { ssr: false },
);
const Polyline = dynamic(
  () => import('react-leaflet').then((mod) => mod.Polyline),
  { ssr: false },
);
const Polygon = dynamic(
  () => import('react-leaflet').then((mod) => mod.Polygon),
  { ssr: false },
);

interface Point {
  lat: number;
  lng: number;
  name?: string;
}

interface TrainLineMapProps {
  points: Point[];
}

function getSquareCorners(center: Point, size: number, angleDeg: number) {
  const angleRad = (angleDeg * Math.PI) / 180;
  const half = size / 2;

  // Calculate the four corners relative to the center
  const corners = (
    [
      [-half, -half],
      [half, -half],
      [half, half],
      [-half, half],
    ] as [number, number][]
  ).map(([dx, dy]) => {
    // Rotate by angle
    const x = dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
    const y = dx * Math.sin(angleRad) + dy * Math.cos(angleRad);
    // Convert meters to lat/lng
    const earthRadius = 6378137; // meters
    const dLat = (y / earthRadius) * (180 / Math.PI);
    const dLng =
      (x / (earthRadius * Math.cos((Math.PI * center.lat) / 180))) *
      (180 / Math.PI);
    return [center.lat + dLat, center.lng + dLng];
  });
  // Close the polygon
  corners.push(corners[0] as [number, number]);
  return corners;
}

function pointToSegmentDistance(p: Point, a: Point, b: Point) {
  // Convert to Cartesian for small distances
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const lat1 = toRad(a.lat),
    lng1 = toRad(a.lng);
  const lat2 = toRad(b.lat),
    lng2 = toRad(b.lng);
  const lat = toRad(p.lat),
    lng = toRad(p.lng);

  // Project point p onto segment ab
  const dLat = lat2 - lat1;
  const dLng = lng2 - lng1;
  const t =
    ((lat - lat1) * dLat + (lng - lng1) * dLng) / (dLat * dLat + dLng * dLng);
  const tClamped = Math.max(0, Math.min(1, t));
  const projLat = lat1 + tClamped * dLat;
  const projLng = lng1 + tClamped * dLng;
  // Haversine distance
  const R = 6371e3;
  const dLatP = lat - projLat;
  const dLngP = lng - projLng;
  const aH =
    Math.sin(dLatP / 2) ** 2 +
    Math.cos(lat) * Math.cos(projLat) * Math.sin(dLngP / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(aH));
}

function MapClickHandler({ onClick }: { onClick: (e: any) => void }) {
  useMapEvent('click', onClick);
  return null;
}

export const TrainLineMap: React.FC<TrainLineMapProps> = ({ points }) => {
  const [selectedPoint, setSelectedPoint] = useState<Point | null>(null);
  const [selectedAngle, setSelectedAngle] = useState<number>(0);
  const [linePoints, setLinePoints] = useState<Point[]>([]);
  const [size, setSize] = useState<number>(2500);
  const [scrollWheelZoom, setScrollWheelZoom] = useState<boolean>(false);
  const polylineRef = useRef<any>(null);
  const [tool, setTool] = useState<'square' | 'insert'>('square');

  useEffect(() => {
    // Create a line connecting all cities
    setLinePoints(points);
  }, [points]);

  const isClient = useIsClient();

  if (!isClient) {
    return <div className="h-[500px] w-full animate-pulse bg-gray-100" />;
  }

  // Calculate center of the map based on all cities
  const center = points.reduce(
    (acc, point) => ({
      lat: acc.lat + point.lat / points.length,
      lng: acc.lng + point.lng / points.length,
    }),
    { lat: 0, lng: 0 },
  );

  const areaKm2 = (size / 1000) ** 2;
  const population = Math.round(areaKm2 * 40000);

  return (
    <div className="flex flex-col">
      <div className="mb-4 flex max-w-xl flex-col justify-stretch gap-4">
        <div className="flex justify-stretch gap-2">
          <label className="w-12">Size:</label>
          <input
            title="Size"
            type="range"
            min={500}
            max={15000}
            step={100}
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
            className="grow"
            style={{ verticalAlign: 'middle' }}
          />
          <span className="w-16 text-right">{size} m</span>
        </div>
        <div className="flex justify-stretch gap-2">
          <label className="w-12">Angle:</label>
          <input
            title="Angle"
            type="range"
            min={-90}
            max={90}
            step={1}
            value={selectedAngle ?? 0}
            onChange={(e) => setSelectedAngle(Number(e.target.value))}
            className="grow"
            style={{ verticalAlign: 'middle' }}
          />
          <span className="w-16 text-right">
            {selectedAngle?.toFixed(0) ?? 0}°
          </span>
        </div>
        <span className="text-gray-600">
          Population: {(population / 2).toLocaleString()} -{' '}
          {population.toLocaleString()}
        </span>
        <div className="hidden items-center gap-2 md:flex">
          <input
            type="checkbox"
            id="scrollWheelZoom"
            checked={scrollWheelZoom}
            onChange={(e) => setScrollWheelZoom(e.target.checked)}
            className="h-4 w-4"
          />
          <label htmlFor="scrollWheelZoom" className="text-sm text-gray-600">
            Enable scroll wheel zoom
          </label>
        </div>
      </div>
      {/* <div className="mb-4 flex gap-4">
        <button
          className={`rounded px-3 py-1 ${tool === 'square' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setTool('square')}
        >
          Put Square
        </button>
        <button
          className={`rounded px-3 py-1 ${tool === 'insert' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setTool('insert')}
        >
          Select Point
        </button>
      </div> */}
      <div className="h-[500px] w-full">
        <MapContainer
          key={`map-${scrollWheelZoom}`}
          center={[center.lat, center.lng]}
          zoom={8}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={scrollWheelZoom}
          zoomControl={true}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <Polyline
            ref={polylineRef}
            positions={linePoints}
            color="blue"
            weight={3}
          />

          {selectedPoint && selectedAngle != null && (
            <Polygon
              positions={
                getSquareCorners(selectedPoint, size, selectedAngle * -1) as [
                  number,
                  number,
                ][]
              }
              color="red"
              fillColor="red"
              fillOpacity={0.2}
            />
          )}

          <MapClickHandler
            onClick={(e: any) => {
              const clickedPoint = { lat: e.latlng.lat, lng: e.latlng.lng };
              if (tool === 'square') {
                setSelectedPoint(clickedPoint);
              } else if (tool === 'insert') {
                // Find the segment where the point should be inserted
                let minDist = Infinity;
                let insertIdx = 0;
                for (let i = 0; i < linePoints.length - 1; i++) {
                  const a = linePoints[i];
                  const b = linePoints[i + 1];
                  if (!a || !b) {
                    continue;
                  }
                  // Distance from point to segment
                  const dist = pointToSegmentDistance(clickedPoint, a, b);
                  if (dist < minDist) {
                    minDist = dist;
                    insertIdx = i + 1;
                  }
                }
                // Insert the point into the array
                const newPoints = [...linePoints];
                newPoints.splice(insertIdx, 0, {
                  ...clickedPoint,
                });
                setLinePoints(newPoints);
              }
            }}
          />
        </MapContainer>
      </div>
      {/* <ul className="mt-8 border-t pt-4 text-sm text-gray-700">
        {JSON.stringify(linePoints)}
      </ul> */}
    </div>
  );
};

export default TrainLineMap;
